import { HOLD_REPEAT_CLICK_SUPPRESS_MS } from '../shared/constants.js';
import { getResourceSeverity, type AlertResource } from '../shared/alerts.js';

export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/**
 * Returns an appropriate console line width (in characters) for the current viewport.
 * The middle zone is ~68% of viewport; Courier New char width is ~60% of font-size;
 * font-size is clamped via CSS at clamp(0.6rem, 2.2vw, 0.9rem).
 */
export const getConsoleLineWidth = (): number => {
  const vw = window.innerWidth;
  if (vw < 380) return 36;
  if (vw < 480) return 42;
  if (vw < 640) return 46;
  if (vw < 800) return 52;
  if (vw < 1000) return 58;
  if (vw < 1200) return 64;
  return 70;
};

/** Formats a console readout line, filling the gap between label and value with dots. */
export const padDots = (label: string, value: string, lineWidth: number): string => {
  const dotsCount = Math.max(1, lineWidth - label.length - value.length);
  return `${label}${'.'.repeat(dotsCount)}${value}`;
};

export const throttle = <TArgs extends unknown[]>(callback: (...args: TArgs) => void, intervalMs: number) => {
  let lastTime = 0;
  return (...args: TArgs): void => {
    const now = Date.now();
    if (now - lastTime < intervalMs) {
      return;
    }
    lastTime = now;
    callback(...args);
  };
};

export const bindHoldRepeatButton = (
  button: HTMLButtonElement,
  onStep: () => void,
  options: { initialDelayMs?: number; repeatDelayMs?: number } = {}
): void => {
  const initialDelayMs = options.initialDelayMs ?? 300;
  const repeatDelayMs = options.repeatDelayMs ?? 90;

  let holdTimeoutId: number | null = null;
  let repeatIntervalId: number | null = null;
  let clearSuppressClickFrameId: number | null = null;
  let suppressNextClick = false;
  let isHolding = false;
  let activePointerId: number | null = null;
  let globalStopHandlersBound = false;

  const clearTimers = (): void => {
    if (holdTimeoutId !== null) {
      window.clearTimeout(holdTimeoutId);
      holdTimeoutId = null;
    }

    if (repeatIntervalId !== null) {
      window.clearInterval(repeatIntervalId);
      repeatIntervalId = null;
    }

    if (clearSuppressClickFrameId !== null) {
      window.clearTimeout(clearSuppressClickFrameId);
      clearSuppressClickFrameId = null;
    }
  };

  const stopHolding = (): void => {
    if (!isHolding && holdTimeoutId === null && repeatIntervalId === null) {
      return;
    }

    isHolding = false;
    activePointerId = null;
    clearTimers();

    if (globalStopHandlersBound) {
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerCancel);
      window.removeEventListener('mouseup', onWindowMouseUp);
      window.removeEventListener('touchend', onWindowTouchEnd);
      window.removeEventListener('touchcancel', onWindowTouchCancel);
      window.removeEventListener('blur', onWindowBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      globalStopHandlersBound = false;
    }

    // The synthetic click follows pointerup/touchend in a later task (on touch, after a
    // frame or more). A requestAnimationFrame cleared the flag too early, letting that click
    // fire a second step. Use a timeout comfortably longer than click latency instead.
    clearSuppressClickFrameId = window.setTimeout(() => {
      suppressNextClick = false;
      clearSuppressClickFrameId = null;
    }, HOLD_REPEAT_CLICK_SUPPRESS_MS);
  };

  const onWindowPointerUp = (event: PointerEvent): void => {
    if (activePointerId !== null && event.pointerId !== activePointerId) {
      return;
    }
    stopHolding();
  };

  const onWindowPointerCancel = (event: PointerEvent): void => {
    if (activePointerId !== null && event.pointerId !== activePointerId) {
      return;
    }
    stopHolding();
  };

  const onWindowMouseUp = (): void => stopHolding();
  const onWindowTouchEnd = (): void => stopHolding();
  const onWindowTouchCancel = (): void => stopHolding();
  const onWindowBlur = (): void => stopHolding();
  const onVisibilityChange = (): void => {
    if (document.hidden) {
      stopHolding();
    }
  };

  const bindGlobalStopHandlers = (): void => {
    if (globalStopHandlersBound) {
      return;
    }

    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerCancel);
    window.addEventListener('mouseup', onWindowMouseUp);
    window.addEventListener('touchend', onWindowTouchEnd);
    window.addEventListener('touchcancel', onWindowTouchCancel);
    window.addEventListener('blur', onWindowBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    globalStopHandlersBound = true;
  };

  const startHolding = (preventDefault: () => void, pointerId: number | null = null): void => {
    preventDefault();
    isHolding = true;
    activePointerId = pointerId;
    clearTimers();
    bindGlobalStopHandlers();
    suppressNextClick = true;
    onStep();

    holdTimeoutId = window.setTimeout(() => {
      repeatIntervalId = window.setInterval(onStep, repeatDelayMs);
      holdTimeoutId = null;
    }, initialDelayMs);
  };

  if ('PointerEvent' in window) {
    button.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0) {
        return;
      }

      startHolding(() => event.preventDefault(), event.pointerId);

      try {
        button.setPointerCapture(event.pointerId);
      } catch {
        // Ignore browsers that do not support pointer capture here.
      }
    });

    button.addEventListener('pointerup', stopHolding);
    button.addEventListener('pointercancel', stopHolding);
    button.addEventListener('lostpointercapture', stopHolding);
  } else {
    button.addEventListener('mousedown', (event) => {
      if (event.button !== 0) {
        return;
      }
      startHolding(() => event.preventDefault());
    });

    button.addEventListener('mouseup', stopHolding);
    button.addEventListener('mouseleave', stopHolding);

    button.addEventListener('touchstart', (event) => {
      startHolding(() => event.preventDefault());
    }, { passive: false });
    button.addEventListener('touchend', stopHolding);
    button.addEventListener('touchcancel', stopHolding);
  }

  button.addEventListener('click', (event) => {
    // detail === 0 means a keyboard/programmatic click, which never had a press step.
    if (suppressNextClick && event.detail !== 0) {
      suppressNextClick = false;
      if (clearSuppressClickFrameId !== null) {
        window.clearTimeout(clearSuppressClickFrameId);
        clearSuppressClickFrameId = null;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    onStep();
  });
};

export const createCard = (_title: string, _description: string): HTMLDivElement => {
  const card = document.createElement('div');
  card.className = 'control-card';
  return card;
};

export const appendDialNotches = (dial: HTMLElement): void => {
  const notches = document.createElement('div');
  notches.className = 'dial-notches';
  for (let index = 0; index < 12; index += 1) {
    const notch = document.createElement('div');
    notch.className = 'dial-notch';
    notch.style.setProperty('--dial-notch-angle', `${index * 30}deg`);
    notches.append(notch);
  }
  dial.append(notches);
};

export const createTelemetryRow = (label: string, initialValue: string) => {
  const row = document.createElement('div');
  row.className = 'sonar-telemetry-row';

  const labelElement = document.createElement('span');
  labelElement.className = 'sonar-telemetry-label';
  labelElement.textContent = label;

  const valueElement = document.createElement('span');
  valueElement.className = 'sonar-telemetry-value';
  valueElement.textContent = initialValue;

  row.append(labelElement, valueElement);
  return { row, valueElement };
};

/** Segment-display color variants. 'yellow' is the shared warning tier; 'red' is critical. */
export type ReadoutVariant = 'amber' | 'cyan' | 'red' | 'yellow';

/**
 * Map a resource value to a readout color via the shared alert thresholds
 * (src/shared/alerts.ts): critical → red, warning → yellow, else the
 * readout's resting color.
 */
export const resourceReadoutVariant = (
  resource: AlertResource,
  value: number,
  nominal: ReadoutVariant = 'amber'
): ReadoutVariant => {
  const severity = getResourceSeverity(resource, value);
  if (severity === 'critical') return 'red';
  if (severity === 'warning') return 'yellow';
  return nominal;
};

export const createReadoutRow = (label: string, value: string, variant: ReadoutVariant = 'amber', unit?: string) => {
  const row = document.createElement('div');
  row.className = 'ctrl-readout-row';

  const labelElement = document.createElement('span');
  labelElement.className = 'ctrl-readout-row-label';
  labelElement.textContent = label;

  const valueElement = createSegDisplay(value, variant);

  if (unit) {
    // Stack the unit beneath the label as a sub-label, matching the left-zone readouts.
    const labelWrap = document.createElement('div');
    labelWrap.className = 'ctrl-readout-row-label-wrap';
    const unitElement = document.createElement('span');
    unitElement.className = 'ctrl-readout-row-unit';
    unitElement.textContent = unit;
    labelWrap.append(labelElement, unitElement);
    row.append(labelWrap, valueElement);
  } else {
    row.append(labelElement, valueElement);
  }

  return { row, valueElement };
};

export type PlusMinusButtonConfig = {
  title?: string;
  ariaLabel?: string;
  disabled?: boolean;
  onStep: () => void;
  holdRepeatOptions?: { initialDelayMs?: number; repeatDelayMs?: number };
  extraClassName?: string;
};

export const createPlusMinusControl = (
  label: string,
  decreaseConfig: PlusMinusButtonConfig,
  increaseConfig: PlusMinusButtonConfig,
  options: { className?: string; buttonRowClassName?: string } = {}
) => {
  const wrap = document.createElement('div');
  wrap.className = `controller-plusminus-control${options.className ? ` ${options.className}` : ''}`;

  const labelElement = document.createElement('span');
  labelElement.className = 'controller-plusminus-label';
  labelElement.textContent = label;

  const buttonRow = document.createElement('div');
  buttonRow.className = `controller-plusminus-button-row${options.buttonRowClassName ? ` ${options.buttonRowClassName}` : ''}`;

  const createButton = (symbol: string, config: PlusMinusButtonConfig): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `controller-plusminus-button${config.extraClassName ? ` ${config.extraClassName}` : ''}`;
    button.textContent = symbol;
    if (config.title) {
      button.title = config.title;
    }
    if (config.ariaLabel) {
      button.setAttribute('aria-label', config.ariaLabel);
    }
    button.disabled = config.disabled ?? false;
    bindHoldRepeatButton(button, config.onStep, config.holdRepeatOptions);
    return button;
  };

  const decreaseButton = createButton('−', decreaseConfig);
  const increaseButton = createButton('+', increaseConfig);

  buttonRow.append(decreaseButton, increaseButton);
  wrap.append(labelElement, buttonRow);

  return { wrap, labelElement, buttonRow, decreaseButton, increaseButton };
};

export const createCircularGauge = (label: string) => {
  const wrap = document.createElement('div');
  wrap.className = 'circular-gauge-wrap';

  const header = document.createElement('div');
  header.className = 'circular-gauge-header';
  header.textContent = label;

  const dial = document.createElement('div');
  dial.className = 'circular-gauge-dial';

  const value = document.createElement('div');
  value.className = 'circular-gauge-value';
  value.textContent = '--';

  const caption = document.createElement('p');
  caption.className = 'circular-gauge-caption';
  caption.textContent = '---';

  dial.append(value);
  appendDialNotches(dial);
  wrap.append(header, dial, caption);

  return { wrap, dial, value, caption };
};

export const createSegDisplay = (value: string, variant: ReadoutVariant = 'amber'): HTMLSpanElement => {
  const display = document.createElement('span');
  display.className = `ctrl-seg-display${variant === 'amber' ? '' : ` ${variant}`}`;
  display.textContent = value;
  return display;
};

export const createBarGauge = (initialPercent = 0) => {
  const track = document.createElement('div');
  track.className = 'ctrl-bar-track';

  const fill = document.createElement('div');
  fill.className = 'ctrl-bar-fill';
  fill.style.width = `${Math.max(0, Math.min(100, initialPercent))}%`;

  track.append(fill);
  return { track, fill };
};

export const createInputCluster = (...elements: HTMLElement[]): HTMLDivElement => {
  const cluster = document.createElement('div');
  cluster.className = 'ctrl-input-cluster';
  if (elements.length > 0) {
    cluster.append(...elements);
  }
  return cluster;
};
